import { useState, useEffect, useRef, useMemo } from "react";
import {
  RiAddLargeLine,
  RiCheckboxMultipleBlankLine,
  RiCheckboxMultipleLine,
  RiCloseLine,
  RiSearchLine,
} from "@remixicon/react";
import { IconLoader2 } from "@tabler/icons-react";
import { AccountBrand } from "../../../types";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BrandFilterChipsProps {
  brands: AccountBrand[];
  selectedBrands: AccountBrand[];
  onBrandToggle: (brand: AccountBrand) => void;
  /** Optional: set multiple brand ids in one call (avoids N toggle calls for select/deselect all) */
  onBrandsSet?: (brandIds: string[]) => void;
  onSelectAll: () => void;
  onAddCompetitorClick: () => void;
  className?: string;
}

const BrandFilterChips = ({
  brands,
  selectedBrands,
  onBrandToggle,
  onBrandsSet,
  onSelectAll,
  onAddCompetitorClick,
  className,
}: BrandFilterChipsProps) => {
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  // Focus search input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const filteredBrands = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return brands;
    return brands.filter((brand) => (brand.name || "").toLowerCase().includes(term));
  }, [brands, searchTerm]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };
    update();
    const onScroll = () => update();
    el.addEventListener("scroll", onScroll, { passive: true } as AddEventListenerOptions);
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [filteredBrands.length, searchOpen]);

  // Determine which brands to check for "all selected" state
  const brandsToCheck = searchTerm.trim() ? filteredBrands : brands;
  const isAllSelected =
    brandsToCheck.length > 0 &&
    brandsToCheck.every((brand) =>
      selectedBrands.some((selected) => selected.id === brand.id),
    );

  const handleSelectAll = () => {
    if (searchTerm.trim()) {
      // If there's a search term, toggle only filtered brands (batch update when possible)
      const filteredIds = new Set(filteredBrands.map((b) => b.id));
      const selectedIds = new Set(selectedBrands.map((s) => s.id));
      if (isAllSelected) {
        // Deselect only filtered brands
        const newBrandIds = selectedBrands
          .filter((s) => !filteredIds.has(s.id))
          .map((s) => s.id);
        if (onBrandsSet) {
          onBrandsSet(newBrandIds);
        } else {
          filteredBrands.forEach((brand) => {
            if (selectedIds.has(brand.id)) onBrandToggle(brand);
          });
        }
      } else {
        // Select only filtered brands
        const newBrandIds = [
          ...selectedBrands.map((s) => s.id),
          ...filteredBrands.filter((b) => !selectedIds.has(b.id)).map((b) => b.id),
        ];
        if (onBrandsSet) {
          onBrandsSet(newBrandIds);
        } else {
          filteredBrands.forEach((brand) => {
            if (!selectedIds.has(brand.id)) onBrandToggle(brand);
          });
        }
      }
    } else {
      // No search term, use the original behavior
      onSelectAll();
    }
  };

  return (
    <TooltipProvider>
      <div className={cn("w-full flex items-center gap-2", className)}>
        {/* Search control */}
        {searchOpen ? (
          <div
            className="bg-white h-7 pl-2 pr-2 py-1 flex items-center gap-[6px] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray"
            style={{ minWidth: 180 }}
          >
            <RiSearchLine size={14} className="text-brand-primary-400 shrink-0" />
            <input
              ref={searchInputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search brands"
              className="outline-none border-0 ring-0 text-[11px] placeholder:text-[11px] leading-[18px] flex-1 text-brand-primary-800 placeholder:text-brand-primary-400 bg-transparent"
            />
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setSearchOpen(false);
              }}
              className="p-0.5"
              aria-label="Clear search and close"
            >
              <RiCloseLine size={14} className="text-brand-primary-400" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            disabled={!brands || brands.length === 0}
            className={cn(
              "bg-white h-7 w-7 shrink-0 p-0 flex items-center justify-center rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray",
              (!brands || brands.length === 0) && "opacity-50 cursor-not-allowed",
            )}
            aria-label="Search brands"
          >
            <RiSearchLine size={14} className="text-[#9AA3BD]" />
          </button>
        )}
        {/* All button */}
        <button
          onClick={handleSelectAll}
          disabled={!brands || brands.length === 0}
          className={cn(
            "bg-white h-7 p-1 pr-[10px] flex items-center gap-[6px] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray text-[11px] text-brand-primary-800 leading-[19px] tracking-[0.02em] transition-colors",
            isAllSelected && "squircle-outline-color-brand-orange",
            (!brands || brands.length === 0) && "opacity-50 cursor-not-allowed",
          )}
        >
          <div
            className="h-[20px] w-[20px] flex justify-center items-center squircle-mask squircle-radius-[4px]"
            style={{ backgroundColor: "#FFFFFF" }}
          >
            {isAllSelected ? (
              <RiCheckboxMultipleLine size={14} color="#FF4500" className="shrink-0" />
            ) : (
              <RiCheckboxMultipleBlankLine
                size={14}
                color="#9AA3BD"
                className="shrink-0"
              />
            )}
          </div>
          <span className="translate-y-[1px]">All</span>
        </button>

        {/* Brand chips */}
        <div className="relative overflow-hidden">
          <div
            ref={scrollRef}
            className="overflow-x-auto flex items-center gap-2 scrollbar-hide"
          >
            {filteredBrands.map((brand) => {
              const isSelected = selectedBrands.some(
                (selected) => selected.id === brand.id,
              );
              const isDisabled = false;
              const isSyncing = brand.sync_status === "pending";

              const buttonElement = (
                <button
                  key={brand.id}
                  onClick={() => onBrandToggle(brand)}
                  disabled={isDisabled}
                  className={cn(
                    "bg-white shrink-0 h-7 p-1 pr-[12px] flex items-center gap-[6px] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray transition-colors whitespace-nowrap relative overflow-hidden",
                    isSelected && "squircle-outline-color-brand-orange",
                    isDisabled && "opacity-50 cursor-not-allowed",
                    isSyncing && "cursor-pointer",
                  )}
                >
                  {isSyncing && (
                    <div className="absolute inset-0 bg-brand-chip-sync-shimmer animate-shimmer-x pointer-events-none" />
                  )}
                  <div className="relative flex items-center gap-[6px]">
                    <img
                      src={brand.profile_image_url || "/default-brand-icon.png"}
                      alt={brand.name}
                      className="w-[20px] h-[20px] object-contain rounded-[6px]"
                    />
                    <span className="text-[11px] font-normal text-brand-primary-800">
                      {brand.name}
                    </span>
                  </div>
                </button>
              );

              if (isDisabled) {
                return (
                  <Tooltip key={brand.id}>
                    <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
                    <TooltipContent side="bottom">
                      This competitor is being added and will be available shortly
                    </TooltipContent>
                  </Tooltip>
                );
              }

              if (isSyncing) {
                return (
                  <Tooltip key={brand.id}>
                    <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
                    <TooltipContent side="bottom" className="flex items-center gap-1">
                      <IconLoader2 size={14} className="animate-spin text-[#FFFFFF99]" />
                      <span>Syncing</span>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return buttonElement;
            })}
          </div>
          {canScrollLeft && (
            <div
              className="absolute left-0 top-0 bottom-0 w-[30px] h-[28px] z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
              }}
            />
          )}
          {canScrollRight && (
            <div
              className="absolute right-0 top-0 bottom-0 w-[30px] h-[28px] z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(270deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
              }}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onAddCompetitorClick}
            className="bg-white h-7 w-7 shrink-0 p-0 flex items-center justify-center rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray text-[11px] text-brand-primary-800 leading-[19px] tracking-[0.02em] transition-colors"
            aria-label="Add competitor"
          >
            <RiAddLargeLine size={14} className="text-brand-primary-400" />
          </button>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default BrandFilterChips;
